/**
 * Viru Trend Dynamics [VTD]
 *
 * Trend-following strategy that pairs a confirmed BUY / SELL entry with full
 * single-position management — entry, protective stop, fixed target and
 * trailing stop — and draws each trade's levels on the chart.
 *
 * Entry engine
 *   ALMA — an Arnaud Legoux moving average (window / offset / sigma) acting as
 *   the trend spine. The legacy ALMA logic is adapted from BOSWaves.
 *   Deviation bands — `alma ± dev x stdev`, the shaded trend ribbon.
 *   ATR-normalised slope — `(alma - alma[slopeLookback]) / slopeLookback / ATR`,
 *   so trend strength is comparable across volatility regimes.
 *
 *   BUY  needs positive slope strength AND a confirmed close above the upper band.
 *   SELL needs negative slope strength AND a confirmed close below the lower band.
 *
 * Position management
 *   One virtual position at a time — while a trade is open no new entry or
 *   reversal is generated. The ribbon may still change colour; that alone does
 *   not close the trade. After an exit the exit candle is skipped and a new
 *   entry is allowed from the next confirmed candle whenever the full setup is
 *   valid again; a fresh trend flip is not required.
 *
 *   Initial stop — prior price structure (recent swing low for a BUY, swing high
 *   for a SELL) clamped between a minimum and maximum ATR distance. That distance
 *   is 1R for the trade.
 *   Target — a fixed 1:1 … 1:6 reward, or "Trail" only.
 *   Trailing — `ATR` follows the best post-entry extreme with an ATR distance,
 *   `ALMA` follows the confirmed ALMA with an ATR buffer. Trailing arms after
 *   the configured favourable R move; the stop may tighten but never loosen.
 *   Trail updates land on the close and apply prospectively — if the close has
 *   already crossed the new trail, a close-price exit is booked.
 *
 * Chart output
 *   Blue entry, orange stop-loss and green target lines per trade; boxed BUY /
 *   SELL labels; exit glyphs (green diamond = target, red cross = initial stop,
 *   amber diamond = trailing exit) and a trend ribbon coloured by slope sign.
 *
 *   If both the stop and the target are touched inside one candle the stop is
 *   assumed first, because the intrabar sequence is unknowable.
 *
 * Category: Trend (listed under the "Strategies" group) | Pane: Overlay
 */

import {
  IndicatorChartLabel,
  IndicatorCurveBand,
  IndicatorDefinition,
  IndicatorPriceLine,
  PlotData,
  PlotPoint,
} from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeATR } from "./atr";
import { getSource } from "./sma";
import { inferTickSize, roundToTick } from "./price-utils";
import { withAlpha } from "../color-utils";

// ─── Trade state ─────────────────────────────────────────────────────────────
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Dir = typeof DIR_LONG | typeof DIR_SHORT;

const EXIT_NONE = 0;
const EXIT_TARGET = 1;
const EXIT_SL = 2;
const EXIT_TRAIL = 3;

export type VtdExitKind = "target" | "sl" | "trail";

export interface VtdTrade {
  index: number;
  dir: Dir;
  entryTime: number;
  entryPrice: number;
  /** Protective stop as first placed — 1R below/above the entry. */
  initialSl: number;
  /** Final protective stop; differs from `initialSl` once trailing has armed. */
  sl: number;
  target: number | null;
  r: number;
  exitTime: number | null;
  exitPrice: number | null;
  exitKind: VtdExitKind | null;
  points: number;
}

export interface VtdDashboard {
  status: string;
  direction: "BUY" | "SELL" | null;
  targetMode: string;
  entry: number | null;
  protectiveSL: number | null;
  target: number | null;
  trailingMethod: string;
  trailActivation: string;
  reentry: string;
  currentPrice: number;
  risk: number | null;
  reward: number | null;
  openPL: number;
  openR: number;
  closedPL: number;
  trades: number;
  wins: number;
  losses: number;
}

const TARGET_RATIOS = [1, 2, 3, 4, 5, 6];

export const viruTrendDynamicsIndicator: IndicatorDefinition = {
  id: "viru_trend_dynamics",
  name: "Viru Trend Dynamics",
  shortName: "VTD",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "source", label: "Source", type: "select", default: "close", group: "ALMA Engine", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
      { label: "HL2", value: "hl2" }, { label: "HLC3", value: "hlc3" },
    ]},
    { key: "almaLength", label: "ALMA Length", type: "number", default: 34, min: 2, max: 500 },
    { key: "almaOffset", label: "ALMA Offset", type: "number", default: 0.85, min: 0, max: 1, step: 0.05 },
    { key: "almaSigma", label: "ALMA Sigma", type: "number", default: 6, min: 0.1, max: 100, step: 0.5 },

    { key: "bandLength", label: "Band Length", type: "number", default: 34, min: 2, max: 500, group: "Deviation Bands" },
    { key: "bandMult", label: "Deviation", type: "number", default: 0.65, min: 0.1, max: 5, step: 0.05 },

    { key: "slopeLookback", label: "Slope Lookback", type: "number", default: 12, min: 1, max: 200, group: "Trend Strength" },
    { key: "slopeThreshold", label: "Slope Threshold", type: "number", default: 0.08, min: 0, max: 2, step: 0.01 },
    { key: "slopeSmooth", label: "Slope Smoothing", type: "number", default: 3, min: 1, max: 50 },
    { key: "atrPeriod", label: "ATR Period", type: "number", default: 14, min: 2, max: 200 },

    { key: "slAtrMult", label: "Stop ATR Mult", type: "number", default: 0.75, min: 0.1, max: 5, step: 0.05, group: "Stop Loss" },
    { key: "minAtrMult", label: "Min ATR Distance", type: "number", default: 0.35, min: 0.05, max: 3, step: 0.05 },
    { key: "maxAtrMult", label: "Max ATR Distance", type: "number", default: 3, min: 0.5, max: 10, step: 0.25 },
    { key: "structureLen", label: "Structure Lookback", type: "number", default: 20, min: 2, max: 200 },

    { key: "targetMode", label: "Target Mode", type: "select", default: "3", group: "Target", options: [
      ...TARGET_RATIOS.map(r => ({ label: `1:${r}`, value: String(r) })),
      { label: "Trail", value: "trail" },
    ]},

    { key: "trailingMethod", label: "Trailing Method", type: "select", default: "ATR", group: "Trailing", options: [
      { label: "ATR", value: "ATR" }, { label: "ALMA", value: "ALMA" }, { label: "Off", value: "Off" },
    ]},
    { key: "trailActivationR", label: "Trail Activation", type: "number", default: 2, min: 0.5, max: 10, step: 0.5 },
    { key: "trailAtrMult", label: "Trail ATR Mult", type: "number", default: 2, min: 0.1, max: 10, step: 0.1 },
    { key: "almaBufferAtr", label: "ALMA Buffer ATR", type: "number", default: 0.25, min: 0, max: 5, step: 0.05 },

    { key: "showRibbon", label: "Show Trend Ribbon", type: "boolean", default: true, group: "Display" },
    { key: "ribbonOpacity", label: "Ribbon Opacity %", type: "number", default: 22, min: 5, max: 80, step: 5 },
    { key: "showAlmaLine", label: "Show ALMA Line", type: "boolean", default: true },
    { key: "showBands", label: "Show Band Lines", type: "boolean", default: true },
    { key: "showLevels", label: "Show Trade Levels", type: "boolean", default: true },
    { key: "maxTradeLevels", label: "Trades Drawn", type: "number", default: 4, min: 1, max: 12, step: 1 },
    { key: "showSignals", label: "Show BUY / SELL", type: "boolean", default: true },
    { key: "showExits", label: "Show Exit Markers", type: "boolean", default: true },
    { key: "labelSize", label: "Label Size", type: "number", default: 11, min: 8, max: 18, step: 1 },
    { key: "recolorCandles", label: "Recolor Candles", type: "boolean", default: false },

    { key: "bullColor", label: "Bullish", type: "color", default: "#089981", group: "Colors" },
    { key: "bearColor", label: "Bearish", type: "color", default: "#F23645" },
    { key: "neutralColor", label: "Neutral", type: "color", default: "#787878" },
    { key: "entryColor", label: "Entry Line", type: "color", default: "#2962FF" },
    { key: "slColor", label: "Stop Line", type: "color", default: "#FF9800" },
    { key: "tpColor", label: "Target Line", type: "color", default: "#26A69A" },
    { key: "trailColor", label: "Trail Exit", type: "color", default: "#FFB300" },

    { key: "useDirectionFilter", label: "Direction Filter", type: "select", default: "Both", group: "Filters", options: [
      { label: "Both", value: "Both" }, { label: "Long Only", value: "Long" }, { label: "Short Only", value: "Short" },
    ]},
    { key: "useSessionFilter", label: "Session Filter", type: "boolean", default: false },
    { key: "sessionStart", label: "Session Start", type: "text", default: "0915" },
    { key: "sessionEnd", label: "Session End", type: "text", default: "1530" },
  ],
  plots: [
    { key: "alma", label: "ALMA", color: "#2962FF", style: "line", lineWidth: 2, lastValueVisible: false, priceLineVisible: false },
    { key: "upperBand", label: "Upper Band", color: "#787878", style: "line", lineWidth: 1, lastValueVisible: false, priceLineVisible: false },
    { key: "lowerBand", label: "Lower Band", color: "#787878", style: "line", lineWidth: 1, lastValueVisible: false, priceLineVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const len = candles.length;
    const empty: PlotData[] = [
      { key: "alma", data: [] },
      { key: "upperBand", data: [] },
      { key: "lowerBand", data: [] },
    ];

    const almaLength = Math.max(2, Math.round(params.almaLength ?? 34));
    const almaOffset = Math.max(0, Math.min(1, Number(params.almaOffset ?? 0.85)));
    const almaSigma = Math.max(0.1, Number(params.almaSigma ?? 6));
    const bandLength = Math.max(2, Math.round(params.bandLength ?? 34));
    const bandMult = Math.max(0.01, Number(params.bandMult ?? 0.65));
    const slopeLookback = Math.max(1, Math.round(params.slopeLookback ?? 12));
    const slopeThreshold = Math.max(0, Number(params.slopeThreshold ?? 0.08));
    const slopeSmooth = Math.max(1, Math.round(params.slopeSmooth ?? 3));
    const atrPeriod = Math.max(2, Math.round(params.atrPeriod ?? 14));

    const slAtrMult = Math.max(0.01, Number(params.slAtrMult ?? 0.75));
    const minAtrMult = Math.max(0, Number(params.minAtrMult ?? 0.35));
    const maxAtrMult = Math.max(minAtrMult, Number(params.maxAtrMult ?? 3));
    const structureLen = Math.max(2, Math.round(params.structureLen ?? 20));

    const targetModeRaw = params.targetMode ?? "3";
    const trailOnly = targetModeRaw === "trail";
    const targetRatio = trailOnly ? 0 : Math.max(1, Number(targetModeRaw) || 3);

    const trailingMethod = params.trailingMethod ?? "ATR";
    const trailActivationR = Math.max(0.5, Number(params.trailActivationR ?? 2));
    const trailAtrMult = Math.max(0, Number(params.trailAtrMult ?? 2));
    const almaBufferAtr = Math.max(0, Number(params.almaBufferAtr ?? 0.25));

    const showRibbon = params.showRibbon !== false;
    const ribbonOpacity = Math.max(0, Math.min(100, params.ribbonOpacity ?? 22)) / 100;
    const showAlmaLine = params.showAlmaLine !== false;
    const showBands = params.showBands !== false;
    const showLevels = params.showLevels !== false;
    const maxTradeLevels = Math.max(1, Math.round(params.maxTradeLevels ?? 4));
    const showSignals = params.showSignals !== false;
    const showExits = params.showExits !== false;
    const labelSize = Math.max(7, Number(params.labelSize) || 11);
    const recolorCandles = params.recolorCandles === true;

    const bullColor = params.bullColor ?? "#089981";
    const bearColor = params.bearColor ?? "#F23645";
    const neutralColor = params.neutralColor ?? "#787878";
    const entryColor = params.entryColor ?? "#2962FF";
    const slColor = params.slColor ?? "#FF9800";
    const tpColor = params.tpColor ?? "#26A69A";
    const trailColor = params.trailColor ?? "#FFB300";

    const directionFilter = params.useDirectionFilter ?? "Both";
    const useSessionFilter = params.useSessionFilter === true;
    const sessionStart = parseHhMm(params.sessionStart ?? "0915");
    const sessionEnd = parseHhMm(params.sessionEnd ?? "1530");

    const warmup = almaLength + slopeLookback + slopeSmooth + 2;
    if (len < warmup + 5) return empty;

    // ── 1. Engine ───────────────────────────────────────────────────────────
    const src = getSource(candles, params.source ?? "close");
    const alma = computeALMA(src, almaLength, almaOffset, almaSigma);
    const stdev = computeStdev(src, bandLength);
    const atr = computeATR(candles, atrPeriod);

    const upper: number[] = new Array(len).fill(NaN);
    const lower: number[] = new Array(len).fill(NaN);
    for (let i = 0; i < len; i++) {
      if (!isFinite(alma[i]) || !isFinite(stdev[i])) continue;
      upper[i] = alma[i] + bandMult * stdev[i];
      lower[i] = alma[i] - bandMult * stdev[i];
    }

    // ATR-normalised slope, lightly smoothed.
    const rawSlope: number[] = new Array(len).fill(NaN);
    for (let i = almaLength - 1 + slopeLookback; i < len; i++) {
      const a = atr[i];
      if (!isFinite(a) || a <= 0 || !isFinite(alma[i - slopeLookback])) continue;
      rawSlope[i] = (alma[i] - alma[i - slopeLookback]) / slopeLookback / a;
    }
    const slope = computeEMA(rawSlope, slopeSmooth);

    // ── 2. Series ───────────────────────────────────────────────────────────
    const almaData: PlotPoint[] = [];
    const upperData: PlotPoint[] = [];
    const lowerData: PlotPoint[] = [];
    for (let i = 0; i < len; i++) {
      const t = candles[i].time;
      if (showAlmaLine && isFinite(alma[i])) almaData.push({ time: t, value: alma[i] });
      if (showBands && isFinite(upper[i])) upperData.push({ time: t, value: upper[i] });
      if (showBands && isFinite(lower[i])) lowerData.push({ time: t, value: lower[i] });
    }

    // ── 3. Trend ribbon — coloured by the slope sign ────────────────────────
    const trendState: number[] = new Array(len).fill(0);
    for (let i = 0; i < len; i++) {
      const s = slope[i];
      if (!isFinite(s) || Math.abs(s) < slopeThreshold) { trendState[i] = 0; continue; }
      trendState[i] = s > 0 ? 1 : -1;
    }

    const curveBands: IndicatorCurveBand[] = [];
    if (showRibbon && upperData.length > 1 && lowerData.length === upperData.length) {
      const barStep = len >= 2 ? Math.max(1, candles[1].time - candles[0].time) : 0;
      const fillSegments: NonNullable<IndicatorCurveBand["segments"]> = [];
      let runStart = 0;
      for (let k = 1; k <= upperData.length; k++) {
        const prevIdx = timeToIndex(candles, upperData[runStart].time);
        const prevState = trendState[prevIdx] ?? 0;
        const state = k < upperData.length ? trendState[timeToIndex(candles, upperData[k].time)] ?? 0 : -2;
        if (state === prevState) continue;
        const endIdx = k - 1;
        const tone = prevState > 0 ? bullColor : prevState < 0 ? bearColor : neutralColor;
        fillSegments.push({
          fromTime: upperData[runStart].time,
          toTime: endIdx + 1 < upperData.length ? upperData[endIdx + 1].time : upperData[endIdx].time + barStep,
          color: withAlpha(tone, ribbonOpacity),
        });
        runStart = k;
      }
      if (fillSegments.length > 0) {
        curveBands.push({
          id: "vtd-ribbon",
          hostPlotKey: "alma",
          upper: upperData,
          lower: lowerData,
          segments: fillSegments,
          borderWidth: 0,
          fill: true,
        });
      }
    }

    // ── 4. Single-position trade manager (closed bars only) ──────────────────
    const lastClosedBar = len - 2;
    const tick = inferTickSize(candles);
    const labels: IndicatorChartLabel[] = [];
    const trades: VtdTrade[] = [];
    const candleColors: { time: number; color: string }[] = [];

    let position: {
      dir: Dir; entryIdx: number; entryTime: number; entry: number;
      initialSl: number; sl: number; target: number | null; r: number;
      bestExtreme: number; trailArmed: boolean;
    } | null = null;
    let skipEntryBar = -1;
    let closedPL = 0;
    let wins = 0;
    let losses = 0;

    for (let i = warmup; i <= lastClosedBar; i++) {
      const c = candles[i];
      const s = slope[i];

      if (recolorCandles) {
        const st = trendState[i];
        candleColors.push({ time: c.time, color: st > 0 ? bullColor : st < 0 ? bearColor : neutralColor });
      }

      // ── Manage an open position ──
      if (position) {
        const isLong = position.dir === DIR_LONG;
        // Favourable excursion, in R.
        const extreme = isLong ? c.high : c.low;
        position.bestExtreme = isLong
          ? Math.max(position.bestExtreme, extreme)
          : Math.min(position.bestExtreme, extreme);
        const favourableR = isLong
          ? (position.bestExtreme - position.entry) / position.r
          : (position.entry - position.bestExtreme) / position.r;

        // Initial stop / fixed target. Stop wins ties — intrabar order is unknown.
        const hitStop = isLong ? c.low <= position.sl : c.high >= position.sl;
        const hitTarget = position.target !== null && (isLong ? c.high >= position.target : c.low <= position.target);

        let exitKind = EXIT_NONE;
        let exitPrice = 0;
        if (hitStop) {
          // Once the trail has armed the stop is a trailing stop, so a stop-out
          // from that level books as a trailing exit, not an initial SL.
          exitKind = position.trailArmed ? EXIT_TRAIL : EXIT_SL;
          exitPrice = position.sl;
        } else if (hitTarget) { exitKind = EXIT_TARGET; exitPrice = position.target as number; }
        else if (trailingMethod !== "Off" && favourableR >= trailActivationR) {
          // Trail candidate from the chosen method, evaluated at the close.
          const a = atr[i] > 0 ? atr[i] : 0;
          let candidate: number | null = null;
          if (trailingMethod === "ALMA" && isFinite(alma[i])) {
            candidate = isLong ? alma[i] - almaBufferAtr * a : alma[i] + almaBufferAtr * a;
          } else {
            candidate = isLong
              ? position.bestExtreme - trailAtrMult * a
              : position.bestExtreme + trailAtrMult * a;
          }
          // Tighten-only.
          if (candidate !== null && (isLong ? candidate > position.sl : candidate < position.sl)) {
            position.sl = roundToTick(candidate, tick);
            position.trailArmed = true;
            // Prospective from here: only a close beyond the new stop exits.
            if (isLong ? c.close < position.sl : c.close > position.sl) {
              exitKind = EXIT_TRAIL;
              exitPrice = c.close;
            }
          }
        }

        if (exitKind !== EXIT_NONE) {
          const points = isLong ? exitPrice - position.entry : position.entry - exitPrice;
          if (points > 0) wins++; else losses++;
          closedPL += points;
          trades.push({
            index: trades.length,
            dir: position.dir,
            entryTime: position.entryTime,
            entryPrice: position.entry,
            // Final protective stop (it may have been trailed up), the 1R
            // distance, and where/why the trade closed.
            initialSl: position.initialSl,
            sl: position.sl,
            target: position.target,
            r: position.r,
            exitTime: c.time,
            exitPrice,
            exitKind: exitKind === EXIT_TARGET ? "target" : exitKind === EXIT_SL ? "sl" : "trail",
            points,
          });
          if (showExits) {
            labels.push({
              time: c.time,
              price: exitPrice,
              shape: exitKind === EXIT_SL ? "cross" : "diamond",
              color: exitKind === EXIT_TARGET ? tpColor : exitKind === EXIT_SL ? bearColor : trailColor,
              offset: 0,
              size: 10,
            });
          }
          position = null;
          skipEntryBar = i; // never re-enter on the exit candle
        }
        continue;
      }

      // ── Flat: look for a fresh entry ──
      if (i === skipEntryBar) continue;
      if (!isFinite(s) || !isFinite(upper[i]) || !isFinite(lower[i])) continue;
      if (directionFilter === "Long" && !(s > slopeThreshold)) continue;
      if (directionFilter === "Short" && !(s < -slopeThreshold)) continue;
      if (useSessionFilter && !inSession(c.time, sessionStart, sessionEnd)) continue;

      let dir: Dir | null = null;
      if (s > slopeThreshold && c.close > upper[i]) dir = DIR_LONG;
      else if (s < -slopeThreshold && c.close < lower[i]) dir = DIR_SHORT;
      if (!dir) continue;

      // Initial stop from prior structure, clamped into an ATR envelope.
      const from = Math.max(0, i - structureLen);
      let structure = c.close;
      for (let j = from; j < i; j++) {
        structure = dir === DIR_LONG ? Math.min(structure, candles[j].low) : Math.max(structure, candles[j].high);
      }
      const a = atr[i] > 0 ? atr[i] : 0;
      let dist = Math.abs(c.close - structure);
      if (a > 0) dist = Math.min(Math.max(dist, minAtrMult * a), maxAtrMult * a);
      if (dist <= 0 || !isFinite(dist)) continue;
      const R = dist;

      const entry = roundToTick(c.close, tick);
      const sl = roundToTick(dir === DIR_LONG ? entry - R : entry + R, tick);
      const target = trailOnly ? null : roundToTick(dir === DIR_LONG ? entry + targetRatio * R : entry - targetRatio * R, tick);

      position = {
        dir,
        entryIdx: i,
        entryTime: c.time,
        entry,
        initialSl: sl,
        sl,
        target,
        r: R,
        bestExtreme: dir === DIR_LONG ? c.high : c.low,
        trailArmed: false,
      };

      if (showSignals) {
        labels.push({
          time: c.time,
          price: dir === DIR_LONG ? c.low : c.high,
          text: dir === DIR_LONG ? "BUY" : "SELL",
          color: dir === DIR_LONG ? bullColor : bearColor,
          textColor: "#FFFFFF",
          position: dir === DIR_LONG ? "belowBar" : "aboveBar",
          size: labelSize,
          offset: Math.max(6, labelSize * 0.7),
        });
      }
    }

    // ── 5. Trade level lines — newest trades only, to stay bounded ──────────
    const priceLines: IndicatorPriceLine[] = [];
    if (showLevels) {
      const visible = trades.slice(-maxTradeLevels);
      for (const t of visible) {
        const isLong = t.dir === DIR_LONG;
        const prefix = `vtd-${t.index}`;
        priceLines.push(
          { id: `${prefix}-entry`, price: t.entryPrice, color: entryColor, title: "Entry", lineStyle: 0, lineWidth: 1, axisLabelVisible: false, fromTime: t.entryTime },
          { id: `${prefix}-sl`, price: t.sl, color: slColor, title: "SL", lineStyle: 0, lineWidth: 1, axisLabelVisible: false, fromTime: t.entryTime },
        );
        if (t.target !== null) {
          priceLines.push({ id: `${prefix}-tp`, price: t.target, color: tpColor, title: "Target", lineStyle: 0, lineWidth: 1, axisLabelVisible: false, fromTime: t.entryTime });
        }
      }
      // The still-open trade keeps its levels on screen.
      if (position) {
        const isLong = position.dir === DIR_LONG;
        priceLines.push(
          { id: "vtd-live-entry", price: position.entry, color: entryColor, title: "Entry", lineStyle: 0, lineWidth: 1, axisLabelVisible: false, fromTime: position.entryTime },
          { id: "vtd-live-sl", price: position.sl, color: position.trailArmed ? trailColor : slColor, title: position.trailArmed ? "Trail" : "SL", lineStyle: 0, lineWidth: 1, axisLabelVisible: false, fromTime: position.entryTime },
        );
        if (position.target !== null) {
          priceLines.push({ id: "vtd-live-tp", price: position.target, color: tpColor, title: "Target", lineStyle: 0, lineWidth: 1, axisLabelVisible: false, fromTime: position.entryTime });
        }
      }
    }

    // ── 6. Dashboard ────────────────────────────────────────────────────────
    const currentPrice = candles[len - 1]?.close ?? 0;
    const liveIdx = Math.max(0, len - 2);
    const liveSlope = slope[liveIdx];
    let status = "WAITING FOR SETUP";
    if (position) {
      status = position.trailArmed ? "TRAILING ACTIVE" : trailOnly ? "TARGET ACTIVE" : "INITIAL SL ACTIVE";
    } else if (isFinite(liveSlope) && liveSlope > slopeThreshold) status = "BULLISH BIAS";
    else if (isFinite(liveSlope) && liveSlope < -slopeThreshold) status = "BEARISH BIAS";

    let openPL = 0;
    let openR = 0;
    if (position) {
      openPL = position.dir === DIR_LONG ? currentPrice - position.entry : position.entry - currentPrice;
      openR = position.r > 0 ? openPL / position.r : 0;
    }

    const dashboard: VtdDashboard = {
      status,
      direction: position ? (position.dir === DIR_LONG ? "BUY" : "SELL") : null,
      targetMode: trailOnly ? "Trail" : `1:${targetRatio}`,
      entry: position ? position.entry : null,
      protectiveSL: position ? position.sl : null,
      target: position ? position.target : null,
      trailingMethod,
      trailActivation: `${trailActivationR}R`,
      reentry: "Confirmed setup after exit",
      currentPrice,
      risk: position ? position.r : null,
      reward: position && position.target !== null
        ? Math.abs(position.target - position.entry)
        : null,
      openPL,
      openR,
      closedPL,
      trades: trades.length,
      wins,
      losses,
    };

    return [
      {
        key: "alma",
        data: almaData,
        curveBands,
        labels,
        priceLines,
        metadata: { dashboard, candleColors, trades },
      },
      { key: "upperBand", data: upperData },
      { key: "lowerBand", data: lowerData },
    ];
  },
};

// ─── Math helpers ────────────────────────────────────────────────────────────

/**
 * Arnaud Legoux Moving Average — Gaussian-weighted, peaking at
 * `m = offset * (length - 1)` with width `s = length / sigma`.
 * Aligned 1:1 with the input; NaN until the first full window.
 */
export function computeALMA(values: number[], length: number, offset: number, sigma: number): number[] {
  const n = values.length;
  const out: number[] = new Array(n).fill(NaN);
  if (n < length) return out;

  const m = offset * (length - 1);
  const s = Math.max(1e-6, length / Math.max(0.1, sigma));
  const w: number[] = new Array(length);
  let wsum = 0;
  for (let i = 0; i < length; i++) {
    w[i] = Math.exp(-((i - m) * (i - m)) / (2 * s * s));
    wsum += w[i];
  }
  if (wsum === 0) return out;

  for (let i = length - 1; i < n; i++) {
    let sum = 0;
    for (let k = 0; k < length; k++) sum += values[i - (length - 1 - k)] * w[k];
    out[i] = sum / wsum;
  }
  return out;
}

/** Rolling sample standard deviation, aligned 1:1 with the input. */
function computeStdev(values: number[], length: number): number[] {
  const n = values.length;
  const out: number[] = new Array(n).fill(NaN);
  let sum = 0;
  let sumSq = 0;
  for (let i = 0; i < n; i++) {
    sum += values[i];
    sumSq += values[i] * values[i];
    if (i >= length) {
      sum -= values[i - length];
      sumSq -= values[i - length] * values[i - length];
    }
    if (i >= length - 1) {
      const mean = sum / length;
      const variance = Math.max(0, sumSq / length - mean * mean);
      out[i] = Math.sqrt(variance);
    }
  }
  return out;
}

/** EMA that tolerates NaN gaps — non-finite inputs simply do not update it. */
function computeEMA(values: number[], period: number): number[] {
  const n = values.length;
  const out: number[] = new Array(n).fill(NaN);
  const k = 2 / (period + 1);
  let ema = NaN;
  let seeded = false;
  let acc = 0;
  let count = 0;
  for (let i = 0; i < n; i++) {
    const v = values[i];
    if (!isFinite(v)) { out[i] = ema; continue; }
    if (!seeded) {
      acc += v;
      count++;
      if (count === period) { ema = acc / period; seeded = true; }
      out[i] = ema;
      continue;
    }
    ema = v * k + ema * (1 - k);
    out[i] = ema;
  }
  return out;
}

/** Map a candle timestamp back to its array index (times ascend). */
function timeToIndex(candles: CandleData[], time: number): number {
  let lo = 0;
  let hi = candles.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (candles[mid].time <= time) lo = mid; else hi = mid - 1;
  }
  return lo;
}

/** "0915" -> 915 minutes; unparseable input falls back to the full day. */
function parseHhMm(value: string): number {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 4).padStart(4, "0");
  const hh = parseInt(digits.slice(0, 2), 10);
  const mm = parseInt(digits.slice(2, 4), 10);
  if (!isFinite(hh) || !isFinite(mm)) return 0;
  return hh * 60 + mm;
}

/** Minutes since IST midnight for a UTC timestamp. */
function istMinutesOfDay(utcSeconds: number): number {
  const IST_OFFSET_SEC = 5 * 3600 + 30 * 60;
  return Math.floor(((utcSeconds + IST_OFFSET_SEC) % 86400 + 86400) % 86400 / 60);
}

/** IST wall-clock membership. Overnight windows (end < start) wrap midnight. */
function inSession(utcSeconds: number, startMin: number, endMin: number): boolean {
  const minutes = istMinutesOfDay(utcSeconds);
  if (endMin === startMin) return true;
  return endMin > startMin
    ? minutes >= startMin && minutes < endMin
    : minutes >= startMin || minutes < endMin;
}